import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/react';
import axios from 'axios';
import { AiOutlineKey, AiOutlineSetting } from 'react-icons/ai';
import { IconCopy, IconOrca, IconTrash } from '../../../Customiz3r/components/ToolSelectorTop/Icons.jsx';
import { toast } from 'react-toastify';
import { ClipboardCheck } from 'lucide-react';
import { confirmDelete, showAlert, showErrorAlert } from '../../../../components/HelperAlerts/HelpersAlerts.jsx';
import { getThemeContext } from '../../../../components/providers/ThemeProvider.jsx';
import CustomOrderRequestModal from '../../../Customiz3r/components/CustomOrderRequestModal.jsx';
import DigitalPriceModal from './DigitalPriceModal.jsx';
import { openModelInOrca } from '../../../../lib/orca.js';
import { canDownloadDesignFiles } from '../../../../lib/permissions.js';

export default function ActionsDropdown({ modelo, navigate, authContext, onDelete, onDuplicated, keyringId, name, compact = false }) {
    const canDownload = canDownloadDesignFiles(authContext);
    const buttonRef = useRef(null);
    const theme = getThemeContext();
    const [loading, setLoading] = useState(false);
    const [requestOpen, setRequestOpen] = useState(false);
    const [digitalOpen, setDigitalOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 8 });

    const updateMenuPosition = () => {
        const button = buttonRef.current;
        if (!button) return;
        const rect = button.getBoundingClientRect();
        const estimatedHeight = authContext.role === 'admin' ? 390 : canDownload ? 300 : 210;
        const opensUp = rect.bottom + estimatedHeight > window.innerHeight;
        const menuWidth = Math.min(208, window.innerWidth - 16);
        // The menu normally opens aligned to the button's right edge, but the
        // first card in a row has no space to its left. Clamp it to the screen.
        const left = Math.min(
            Math.max(8, rect.right - menuWidth),
            window.innerWidth - menuWidth - 8,
        );
        setMenuPosition({
            top: Math.max(8, opensUp ? rect.top - estimatedHeight - 8 : rect.bottom + 8),
            left,
        });
    };

    useEffect(() => {
        const handleViewportChange = () => updateMenuPosition();
        window.addEventListener('resize', handleViewportChange);
        window.addEventListener('scroll', handleViewportChange, true);
        return () => {
            window.removeEventListener('resize', handleViewportChange);
            window.removeEventListener('scroll', handleViewportChange, true);
        };
    }, []);

    const handleDuplicate = async () => {
        const isConfirmed = await confirmDelete({
            title: 'Duplicar llavero',
            text: '¿Está seguro de que desea duplicar este llavero?',
            theme,
            confirmButtonText: 'Sí, duplicar',
        });
        if (!isConfirmed) return;
        try {
            setLoading(true);
            const response = await axios.post(`/customized-keyrings/duplicate/${keyringId}/`);
            toast.success('Llavero duplicado con éxito', { theme: theme === 'dark' ? 'dark' : 'light' });
            onDuplicated?.(response.data);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al duplicar el llavero', { theme: theme === 'dark' ? 'dark' : 'light' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        const isConfirmed = await confirmDelete({
            title: 'Eliminar modelo 3D',
            text: `¿Está seguro de que desea eliminar el modelo 3D ${modelo.id}?`,
            theme,
        });
        if (!isConfirmed) return;
        try {
            await axios.delete(`/customized-keyrings/${modelo.id}/`);
            onDelete?.(modelo.id);
            showAlert({ title: 'Eliminado', text: `Modelo ${modelo.name} eliminado exitosamente.`, icon: 'success', theme });
        } catch (error) {
            showErrorAlert({ text: 'Hubo un error al eliminar el modelo.', theme });
        }
    };

    const handleOrca = async () => {
        try {
            setLoading(true);
            await openModelInOrca(keyringId);
        } catch (error) {
            console.error('Error al abrir en Orca:', error);
            toast.error(error.response?.data?.detail || 'No se pudo abrir el modelo en Orca', { theme: theme === 'dark' ? 'dark' : 'light' });
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        try {
            const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;
            const fileUrl = `${API_BASE_URL}customized-keyrings/${keyringId}/3mftoken/`;
            // Con la pausa para meter imanes, NFC o la arandela del abrebotellas,
            // como la descarga del editor. Sin cavidades no se añade ninguna.
            const response = await axios.get(fileUrl, { responseType: 'blob', params: { insert_pause: 1 } });
            const disposition = response.headers['content-disposition'];
            let fileName = `keyring_${name}_${keyringId}.3mf`;
            if (disposition) {
                const match = disposition.match(/filename="(.+)"/);
                if (match) fileName = match[1];
            }
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al descargar:', error);
            if (error.response?.status === 404) alert('Archivo no encontrado en el servidor.');
            else alert('Error al descargar el archivo.');
        }
    };

    const handleGenerateToken = async () => {
        try {
            setLoading(true);
            const response = await axios.post(`/generate-public-token/${keyringId}/`, { expiration_seconds: 3600, max_usage: 0 });
            let publicUrl = response.data.public_url;
            toast.success('Enlace público generado exitosamente', { theme: theme === 'dark' ? 'dark' : 'light' });
            publicUrl = `${window.location.origin}/${String(publicUrl).replace(/^\/+/, '')}`;
            await navigator.clipboard.writeText(publicUrl);
            showAlert({ title: 'Enlace generado', text: `Copiado al portapapeles:
${publicUrl}`, icon: 'success' });
        } catch (error) {
            console.error('Error al generar token:', error);
            toast.error(error.response?.data?.detail || 'Error al generar el enlace público', { theme: theme === 'dark' ? 'dark' : 'light' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <Menu as="div" className="relative inline-block text-left z-50">
            {({ open }) => (
                <>
                    <MenuButton ref={buttonRef} onClick={updateMenuPosition} className={`btn btn-sm btn-secondary ${compact ? 'btn-square' : ''}`}>
                        <AiOutlineSetting size={20} />
                    </MenuButton>
                    {open && createPortal(
                            <MenuItems
                                static
                                className="fixed w-52 max-w-[calc(100vw-1rem)] max-h-[calc(100dvh-1rem)] overflow-y-auto bg-white dark:bg-base-100 divide-y rounded-md shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none z-[9999]"
                                style={{ top: menuPosition.top, left: menuPosition.left }}
                            >
                    <div className="p-2 grid gap-2 right-0 top-0 z-40">
                        <MenuItem>
                            {({ active }) => (
                                <button onClick={() => setRequestOpen(true)} className={`flex items-center gap-2 p-2 rounded ${active ? 'bg-secondary/10' : ''}`}>
                                    <ClipboardCheck className="w-5 h-5" />
                                    <span>Pedir este diseño</span>
                                </button>
                            )}
                        </MenuItem>
                        <MenuItem>
                            {({ active }) => (
                                <button onClick={() => setDigitalOpen(true)} className={`flex items-center gap-2 p-2 rounded ${active ? 'bg-secondary/10' : ''}`}>
                                    <AiOutlineSetting className="w-5 h-5" />
                                    <span>Precio digital (estimado)</span>
                                </button>
                            )}
                        </MenuItem>
                        {/* El proyecto Orca (con presets A1 Mini/PLA) y la descarga
                            son para quien puede descargar (C3D-27), no para el
                            propietario sin más. */}
                        {canDownload && (
                            <MenuItem>
                                {({ active }) => (
                                    <button onClick={handleOrca} disabled={loading} className={`flex items-center gap-2 p-2 rounded ${active ? 'bg-secondary/10' : ''}`}>
                                        <IconOrca className="w-5 h-5" />
                                        <span>{loading ? 'Preparando...' : 'Abrir con Orca'}</span>
                                    </button>
                                )}
                            </MenuItem>
                        )}
                        {canDownload && (
                            <MenuItem>
                                {({ active }) => (
                                    <button onClick={handleDownload} className={`flex items-center gap-2 p-2 rounded ${active ? 'bg-secondary/10' : ''}`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                        </svg>
                                        <span>Descargar</span>
                                    </button>
                                )}
                            </MenuItem>
                        )}
                        {authContext.role === 'admin' && (
                            <>
                                <MenuItem>
                                    {({ active }) => (
                                        <button onClick={handleDuplicate} disabled={loading} className={`flex items-center gap-2 p-2 rounded ${active ? 'bg-secondary/10' : ''}`}>
                                            <IconCopy className="w-5 h-5" />
                                            <span>{loading ? 'Duplicando...' : 'Duplicar Modelo'}</span>
                                        </button>
                                    )}
                                </MenuItem>
                                <MenuItem>
                                    {({ active }) => (
                                        <button onClick={handleGenerateToken} disabled={loading} className={`flex items-center gap-2 p-2 rounded ${active ? 'bg-secondary/10' : ''}`}>
                                            <AiOutlineKey className="w-5 h-5" />
                                            <span>{loading ? 'Generando...' : 'Crear enlace público'}</span>
                                        </button>
                                    )}
                                </MenuItem>
                            </>
                        )}
                        <MenuItem>
                            {({ active }) => (
                                <button onClick={handleDelete} className={`flex items-center gap-2 p-2 rounded ${active ? 'bg-red-500/40 text-white' : ''}`}>
                                    <IconTrash className="w-5 h-5" />
                                    <span>Eliminar</span>
                                </button>
                            )}
                        </MenuItem>
                    </div>
                            </MenuItems>,
                            document.body,
                        )}
                </>
            )}
        </Menu>
        {requestOpen && (
            <CustomOrderRequestModal
                keyring={modelo}
                onClose={() => setRequestOpen(false)}
            />
        )}
        <DigitalPriceModal
            keyringId={keyringId}
            open={digitalOpen}
            onClose={() => setDigitalOpen(false)}
        />
        </>
    );
}
